import { isNotNil } from '@/utils/object-util'

/**
 * Rounds a number to the given precision.
 */
export function roundToPrecision(value: number, precision = 2) {
  return Number(Math.round(Number(value + 'e' + precision)) + 'e-' + precision)
}

/**
 * Cuts off the remaining digits.
 */
export function toPrecision(value: number | null | undefined, precision = 2) {
  if (isNotNil(value)) {
    return Number(
      Math.floor(Number(value + 'e' + precision)) + 'e-' + precision,
    )
  }
  return Number.NaN
}

/**
 * Formats a number to locale string.
 *
 * @param {number} value
 * @param {number | undefined} precision
 */
export function numberToLocaleString(value: number, precision?: number) {
  if (!value) {
    value = 0
  }

  return value.toLocaleString(
    undefined,
    typeof precision === 'number'
      ? {
          minimumFractionDigits: precision,
          maximumFractionDigits: precision,
        }
      : undefined,
  )
}

/**
 * Determines if the string is a valid number.
 * @param str
 */
export function isValidNumberString(str: string) {
  return /^-?\d+(\.\d+)?$/.test(str)
}

/**
 * Numeric separators.
 */
export interface NumericSeparators {
  thousand: string
  decimal: string
}

/**
 * Gets the separators to use for formatting numbers based on locale.
 */
export function getLocaleNumericSeparators(locale?: string): NumericSeparators {
  // Used to extract the separators.
  const PROBE = (1234.5).toLocaleString(locale)
  // The decimal separator is just 1 character before the last character.
  const decimal = PROBE.slice(PROBE.length - 2, PROBE.length - 1)
  // The thousands searator is 1 character after the first character.
  const thousand = PROBE.slice(1, 2)
  return { thousand, decimal }
}

/**
 * Parses a number from a partial string, using the separators to determine validity.
 *
 * @param {*} str
 * @param {*} separators
 * @returns if valid, returns the `Number`, otherwise `NaN`.
 */
export function parseNumberFromPartialString(
  str: string | number,
  separators: NumericSeparators = getLocaleNumericSeparators(),
) {
  const MINUS = '-'
  if (typeof str === 'number') {
    return str
  }

  if (!str) {
    return Number.NaN
  }

  let buf = ''
  const lastIndex = str.length - 1
  let foundDecimal = false
  let foundMinus = false
  for (let i = 0; i <= lastIndex; i++) {
    const ch = str[i]
    if (ch === MINUS && !foundMinus) {
      foundMinus = true
      buf += MINUS
    } else if (ch === separators.decimal) {
      if (foundDecimal) {
        break
      }
      foundDecimal = true
      buf += '.'
    } else if (isDigit(ch)) {
      buf += ch
    }
  }

  return parseFloat(buf)
}

/**
 * Determines if the character is a digit.
 * @param {*} ch
 */
export function isDigit(ch: string) {
  const c = ch.charCodeAt(0)
  // 48 = '0'
  // 57 = '9'
  return c >= 48 && c <= 57
}
